// Session persistence (PRD 002 F7). Mirrors the durable slice of the store —
// finalized segments + speakers + the AI's inline contributions — to IndexedDB
// so a mid-session reload restores the whole feed (read-only; live capture needs
// an explicit resume that re-acquires the mic, and the bridge client resumes its
// signal drain at the gateway's live edge so restored turns aren't re-delivered).
// Interim text, level, connection, and the bridge delivery receipts are volatile
// and never persisted.
//
// We gate saves on the REFERENTIAL identity of finals/speakers/aiTurns (the store
// keeps those stable unless they actually change), so the ~8/s level ticks don't
// thrash IndexedDB or starve the debounce.

import type { AiTurn } from '@/lib/ai/types'
import { openDb } from '@/lib/idb/idb'
import type { Speaker, SpeakerId, TranscriptSegment } from '@/lib/transcription/types'
import type { SessionStore } from './sessionStore'
import { remotePutSession } from './sessionsRemote'

const SAVE_DEBOUNCE_MS = 1000

// Ids deleted this session: a late debounced/dispose flush must NOT resurrect a
// record the user just deleted (the active store may still be alive briefly).
const deletedIds = new Set<string>()

export interface PersistedSession {
  id: string
  title: string
  createdAt: number
  updatedAt: number
  finals: TranscriptSegment[]
  speakers: Record<SpeakerId, Speaker>
  /** The AI participant's inline contributions (cards / present bubbles). Older
   *  records predate this field, so restore treats it as optional. */
  aiTurns?: AiTurn[]
}

function toRecord(store: SessionStore): PersistedSession {
  const s = store.getSnapshot()
  return {
    id: s.id,
    title: s.title,
    createdAt: s.createdAt,
    updatedAt: Date.now(),
    finals: s.finals,
    speakers: s.speakers,
    aiTurns: s.aiTurns
  }
}

async function write(record: PersistedSession): Promise<void> {
  if (deletedIds.has(record.id)) return // don't resurrect a deleted session
  const db = await openDb()
  await db.put('sessions', record, record.id)
  // Mirror to the server (the system of record, PRD 011): IndexedDB is now the
  // local cache. Best-effort — a failed sync leaves the local copy intact.
  void remotePutSession(record)
}

/** Subscribe persistence to a store. Returns a detach fn that also flushes the
 *  final state synchronously-ish (fire-and-forget the last write). */
export function attachPersistence(store: SessionStore): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastFinals = store.getSnapshot().finals
  let lastSpeakers = store.getSnapshot().speakers
  let lastTitle = store.getSnapshot().title
  let lastAiTurns = store.getSnapshot().aiTurns

  function flush(): void {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    void write(toRecord(store))
  }

  const unsubscribe = store.subscribe(() => {
    const s = store.getSnapshot()
    if (
      s.finals === lastFinals &&
      s.speakers === lastSpeakers &&
      s.title === lastTitle &&
      s.aiTurns === lastAiTurns
    ) {
      return
    }
    lastFinals = s.finals
    lastSpeakers = s.speakers
    lastTitle = s.title
    lastAiTurns = s.aiTurns
    if (timer) clearTimeout(timer)
    timer = setTimeout(flush, SAVE_DEBOUNCE_MS)
  })

  return () => {
    unsubscribe()
    flush()
  }
}

export async function loadPersistedSession(
  id: string
): Promise<PersistedSession | undefined> {
  const db = await openDb()
  return db.get<PersistedSession>('sessions', id)
}

/** All persisted sessions, newest-CREATED first. Ordering by creation (not
 *  update) keeps the sidebar stable — resuming a session no longer jumps it to
 *  the top. */
export async function listPersistedSessions(): Promise<PersistedSession[]> {
  const db = await openDb()
  const all = await db.getAll<PersistedSession>('sessions')
  return all.sort((a, b) => b.createdAt - a.createdAt)
}

/** Delete a session for good. Guards against a late flush resurrecting it. */
export async function deletePersistedSession(id: string): Promise<void> {
  deletedIds.add(id)
  const db = await openDb()
  await db.delete('sessions', id)
}

/** Rename a stored (not-currently-live) session. For a live session, call
 *  store.setTitle instead so the open view updates and persists in step. */
export async function renamePersistedSession(id: string, title: string): Promise<void> {
  const next = title.trim()
  if (!next) return
  const db = await openDb()
  const rec = await db.get<PersistedSession>('sessions', id)
  if (!rec) return
  await db.put('sessions', { ...rec, title: next, updatedAt: Date.now() }, id)
}
